import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  LinearProgress,
  MenuItem,
  FormControlLabel,
} from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import styles from './QAPhotoUploadDialogMenuItem.styles';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import { AppContext } from '../../../../../AppContext';
import { OrderReviewContext, UPDATE_IMAGES } from '../../../OrderReviewContext';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

// SERVICES
import { saveAnswers, saveUploadedImagesWarnDuplicates } from '../../../../../services';
import ReactSelect from 'react-select';
import { cloneDeep } from 'lodash';

const QAPhotoUploadDialogMenuItem = ({ classes, onClose }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [duplicatesModal, setDuplicatesModal] = useState(null); // { title, message, duplicates } or null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [files, setFiles] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOrderFormHeader, setSelectedOrderFormHeader] = useState(null);

  const { dispatch: appDispatch } = useContext(AppContext);
  const {
    state: { order, formHeaders, orderableFormHeaders },
    dispatch,
  } = useContext(OrderReviewContext);

  const { getRootProps, getInputProps } = useDropzone({
    accept: '.jpg, .jpeg',
    onDrop: acceptedFiles => {
      setFiles(
        acceptedFiles.map((file, fileIndex) => {
          const fileType = file.name.split('.').slice(-1);

          return Object.assign(file, {
            imageUri: URL.createObjectURL(file),
            fileName: `${new Date().getTime()}${fileIndex}.${fileType}`,
            label: null,
          });
        }),
      );
    },
  });

  const createImageObject = image => {
    return {
      formQaImageId: image.formFieldRepImageId,
      imageUri: image.imageUri,
      edittedFromId: null,
      hideImage: false,
      rotateDegree: 0,
      label: null,
      description: null,
    };
  };

  const createNewFormDefinitionAnswer = (fd, image) => {
    const newAnswer = {
      orderFormHeaderId: fd.orderFormHeaderId,
      formDefinitionId: fd.formDefinitionId,
      questionId: fd.questionId,
      formQaAnswerId: null,
      type: fd.answerType,
      valueId: null,
      value: null,
      text: null,
      modifiedOn: new Date(),
      images: image,
      default: false,
    };
    return newAnswer;
  };

  const uploadPhotoWithLocation = images => {
    // we need the image and the location
    // creating a new object to be added, this helps re-render when state is updated
    let newImages = images.map(i => {
      return createImageObject(i);
    });

    // get the supplement to add
    let formHeaderToAddImage = formHeaders.find(fh => fh.orderFormHeaderId === selectedOrderFormHeader);

    let selectedAnswers = [];

    // find the question to be added everytime an upload occurs with a selection
    let supplementToAdd = formHeaderToAddImage.formDefinitions.find(fd => fd.questionId === 9374); // 9374 is the questionid of additional photo

    // formHeaderToAddImage.formDefinitions.forEach(fd => {
    //   selectedAnswers = selectedAnswers.concat(fd.answers);

    //   if (fd.answers.length === 0) {
    //     const newAnswer = createNewFormDefinitionAnswer(fd, newImage);
    //     selectedAnswers.push(newAnswer);
    //   }
    // });
    selectedAnswers = selectedAnswers.concat(supplementToAdd.answers);
    if (supplementToAdd.answers.length === 0) {
      let newAnswer = createNewFormDefinitionAnswer(supplementToAdd, newImages);
      selectedAnswers.push(newAnswer);
    }

    // add the image
    selectedAnswers.concat(
      supplementToAdd.answers.map(sa => {
        let updatedAnswer = sa;
        updatedAnswer.images = updatedAnswer.images.concat(newImages);
        return updatedAnswer;
      }),
    );

    // logic from updateFormHeaderImageLocations()
    let updatedFormHeaders = JSON.parse(JSON.stringify(formHeaders));
    updatedFormHeaders.forEach(ufh => {
      if (ufh.formDefinitions) {
        ufh.formDefinitions = ufh.formDefinitions.map(fd => {
          if (fd.questionCategoryId !== 1) {
            return fd;
          }

          if (fd.answers.length > 0) {
            fd.answers = fd.answers
              .map(a => {
                a.images = a.images.filter(
                  i =>
                    !newImages.some(ni => ni.formQaImageId === i.formQaImageId) ||
                    newImages.some(
                      ni =>
                        ni.formQaImageId === i.formQaImageId &&
                        selectedAnswers.some(
                          sa =>
                            sa.orderFormHeaderId === a.orderFormHeaderId && sa.formDefinitionId === a.formDefinitionId,
                        ),
                    ),
                );
                return a;
              })
              .filter(a => a.images.length > 0);
          } else {
            fd.answers = selectedAnswers.filter(
              sa => sa.orderFormHeaderId == fd.orderFormHeaderId && sa.formDefinitionId == fd.formDefinitionId,
            );
          }
          return fd;
        });
      }
    });

    // update and save
    dispatch({
      type: 'OVERRIDE_FORM_HEADERS',
      formHeaders: updatedFormHeaders,
    });
    saveAnswers(selectedAnswers, newImages);
  };

  const uploadPhotos = async () => {
    // add new images to the misc images array
    // then save to the backend which will generate IDs for the images

    const tempOrderForms = JSON.parse(JSON.stringify(formHeaders));

    tempOrderForms.map(form => {
      form.formHeaderName = form.name;
    });

    // console.log(tempOrderForms);

    if (!tempOrderForms.length) {
      return console.log('no order forms to append to...');
    }

    // tempOrderForms[0].miscImages = tempOrderForms[0].miscImages ? tempOrderForms[0].miscImages.concat(...files) : files;

    try {
      setIsSubmitting(true);
      const newImages = await saveUploadedImagesWarnDuplicates(tempOrderForms, order.id, files, selectedOrderFormHeader);
      if (selectedOrderFormHeader !== null) {
        uploadPhotoWithLocation(newImages.images);
      }

      dispatch({
        type: UPDATE_IMAGES,
        images: newImages.images,
      });
      if (newImages.duplicates && newImages.duplicates.length > 0) {
        setDuplicatesModal({
          title: 'Some images were not uploaded.',
          message: `The other images in your batch were uploaded successfully. The following were skipped because the names are duplicates of existing images:`,
          duplicates: newImages.duplicates,
        });
        setSubmitError(null);
        setIsSubmitting(false);
        handleDialogClose(false); // don't notify parent — keep popover mounted so duplicates modal stays visible
        return;
      }

      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'success',
          message: 'Pictures uploaded successfully',
        },
      });

      // get the image carousel and scroll to the beginning
      const imageCarouselRef = document.getElementById('image-carousel-scrollview');
      if (imageCarouselRef) {
        imageCarouselRef.scrollTo({ left: 0, behavior: 'smooth' });
      }
      // messagesContainerRef.scrollTo({ top: messagesContainerRef.scrollHeight, behavior: 'smooth' });

      // clear state
      setFiles([]);
      setSubmitError(null);
      setSubmitMessage(null);
      handleDialogClose();
    } catch (error) {
      console.log(error);
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: 'An error occurred while uploading your images... Please try again',
        },
      });
    }
    setIsSubmitting(false);
  };

  const handleRemoveFileFromPreview = file => {
    let newFiles = files.filter(f => f.fileName !== file.fileName);

    if (files.length === 1) {
      setFiles([]);
    } else {
      setFiles(newFiles);
    }
  };

  const renderThumb = (file, fileIndex) => (
    <>
      <div
        key={fileIndex}
        style={{ position: 'relative', display: 'inline-block', marginRight: 15, marginBottom: 15 }}
        onMouseEnter={() => setHoveredIndex(fileIndex)}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <img src={file.imageUri} style={{ maxWidth: 125 }} />
        {hoveredIndex === fileIndex && (
          <div
            style={{
              position: 'absolute',
              top: -5,
              right: -5,
              color: 'white',
              background: 'red',
              height: 20,
              width: 20,
              borderRadius: '100%',
            }}
            onClick={() => handleRemoveFileFromPreview(file)}
          >
            <CloseIcon style={{ fontSize: '20px' }} />
          </div>
        )}
      </div>
    </>
  );

  // const thumbs = files.map(file => (
  //   <li key={file.path}>
  //     {file.path} - {file.size} bytes
  //   </li>
  // ));

  // console.log(thumbs);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = (notifyParent = true) => {
    setDialogOpen(false);
    setFiles([]);
    if (notifyParent) !!onClose && onClose();
    setSelectedOrderFormHeader(null);
  };

  useEffect(() => {
    let t = formHeaders.filter(fh =>
      orderableFormHeaders.some(ofh => ofh.name === fh.name || '*' + ofh.name === fh.name),
    );

    // Create a count of the names
    let nameCounts = t.reduce((acc, cur) => {
      acc[cur.name] = acc[cur.name] ? acc[cur.name] + 1 : 1;
      return acc;
    }, {});

    // Create an index count for duplicates
    let duplicates = {};

    t = t.map(x => {
      let name = x.name;
      if (nameCounts[name] > 1) {
        let count = duplicates[name] || 0;
        duplicates[name] = count + 1;
        name = `${name} #${count + 1}`;
      }

      return {
        value: x.orderFormHeaderId,
        label: name,
      };
    });

    setOptions(t);
  }, []);

  return (
    <React.Fragment>
      <MenuItem onClick={handleDialogOpen}>Add Photos</MenuItem>

      <Dialog
        open={dialogOpen}
        fullWidth={true}
        maxWidth="lg"
        onClose={handleDialogClose}
        aria-labelledby="image-modal-title"
        className={classes.qaPhotoUploadDialog}
        autoScrollBodyContent={false}
        contentStyle={{ width: '100%', maxWidth: 'none' }}
      >
        <DialogTitle>Photo Upload</DialogTitle>
        <DialogContent>
          <label style={{ paddingLeft: 20, paddingRight: 20, fontWeight: 'bold' }}>
            Optional: Select form to attach photos to
          </label>
          <ReactSelect
            className={classnames('col-center-center', classes.dropdown)}
            options={options}
            menuPosition="fixed"
            onChange={ev => setSelectedOrderFormHeader(ev.value)}
          />
          <main className={classnames('col-center-center', classes.container)}>
            <div {...getRootProps({ className: classes.dropzone })}>
              <input {...getInputProps()} />
              <p>Drag 'n' drop some files here, or click to select files</p>
            </div>

            <h2>Preview</h2>
            <div className={classnames('row-start-center')} style={{ flexWrap: 'wrap' }}>
              {!!files.length && files.map(renderThumb)}
              {!files.length && <div style={{ color: '#777' }}>Please add some photos above</div>}
            </div>
          </main>
        </DialogContent>
        <DialogActions
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Button
            disabled={isSubmitting}
            onClick={handleDialogClose}
            variant="contained"
            style={{ backgroundColor: '#315382', color: 'white' }}
          >
            Cancel
          </Button>
          <div style={{ flex: 1, paddingLeft: 10, paddingRight: 10 }}>
            {isSubmitting && <LinearProgress />}
            {submitMessage && <div>{submitMessage}</div>}
            {submitError && typeof submitError !== 'object' && (
              <div style={{ color: 'red' }}>{submitError}</div>
            )}
          </div>
          <Button
            disabled={isSubmitting}
            onClick={uploadPhotos}
            variant="contained"
            style={{ backgroundColor: '#315382', color: 'white' }}
          >
            Upload Photos
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!duplicatesModal}
        onClose={() => setDuplicatesModal(null)}
        aria-labelledby="duplicates-modal-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="duplicates-modal-title">{duplicatesModal?.title}</DialogTitle>
        <DialogContent>
          {duplicatesModal?.message && (
            <p style={{ marginBottom: 12 }}>{duplicatesModal.message}</p>
          )}
          {duplicatesModal?.duplicates?.length > 0 && (
            <ul
              style={{
                margin: 0,
                paddingLeft: 24,
                maxHeight: 360,
                overflowY: 'auto',
              }}
            >
              {duplicatesModal.duplicates.map((d, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  {typeof d === 'string' ? d : d.name ?? d.fileName ?? String(d)}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicatesModal(null)}
            variant="contained"
            style={{ backgroundColor: '#315382', color: 'white' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

QAPhotoUploadDialogMenuItem.defaultProps = {};
QAPhotoUploadDialogMenuItem.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default withStyles(styles)(QAPhotoUploadDialogMenuItem);
